import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import useToasts from '@/hooks/useToasts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  title: string;
  description: string;
};

interface Props {
  onClose: () => void;
  title?: string;
  description?: string;
  translationId: number;
}

const TitleDescriptionForm: FC<Props> = ({
  onClose,
  title,
  description,
  translationId,
}) => {
  const alerts = useAlerts();
  const toasts = useToasts();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      title: title ?? '',
      description: description ?? '',
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.put(`/v1/distribution/ml-translations/${translationId}`, data);

      await queryClient.invalidateQueries([
        'ml_translations',
        { id: translationId.toString() },
      ]);

      onClose();

      toasts.success(undefined, 'Translations saved');
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true, true);
    }
  });

  return (
    <Modal>
      <SidePanel heading={'Edit translations'} onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                label="Title"
                name="title"
              />
              <FormField<FormDataT>
                control={control}
                label="Description"
                type="textarea"
                name="description"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Save
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default TitleDescriptionForm;
